/**
 * @module features/clients/api/clientQueries
 * @description TanStack Query hooks and API clients for Super Admin Client Organization Management.
 * Enforces Constitution Principles I, II & XIII (standard envelope, X-Request-Id, error mapping).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiSuccessEnvelope } from "@/lib/api-client";
import {
  ClientSummary,
  ClientDetail,
  ClientLinkedUser,
  AvailableUser,
  ClientFleetStats,
  ClientOnboardFormValues,
  ClientUpdateFormValues,
} from "../types";

export interface ClientsQueryParams {
  page?: number;
  page_size?: number;
  status?: string;
  search?: string;
}

export interface PaginatedClientsResponse {
  items: ClientSummary[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

// ============================================================================
// API Functions
// ============================================================================

export async function getClients(
  params: ClientsQueryParams = {}
): Promise<PaginatedClientsResponse> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set("page", String(params.page));
  if (params.page_size) queryParams.set("page_size", String(params.page_size));
  if (params.status && params.status !== "all") queryParams.set("status", params.status);
  if (params.search) queryParams.set("search", params.search);

  const url = `/super-admin/clients/?${queryParams.toString()}`;
  const response = await apiClient.get<
    ApiSuccessEnvelope<any> & {
      pagination?: PaginatedClientsResponse["pagination"];
    }
  >(url);

  const rawData = response.data?.data;
  const items: ClientSummary[] = Array.isArray(rawData)
    ? rawData
    : Array.isArray(rawData?.items)
    ? rawData.items
    : [];

  const pagination =
    rawData?.pagination ||
    response.data?.pagination || {
      page: params.page || 1,
      page_size: params.page_size || 10,
      total_count: items.length,
      total_pages: 1,
      has_next: false,
      has_prev: false,
    };

  return {
    items,
    pagination,
  };
}

export async function getClientDetail(clientId: string): Promise<ClientDetail> {
  const response = await apiClient.get<ApiSuccessEnvelope<ClientDetail>>(
    `/super-admin/clients/${clientId}/`
  );
  return response.data.data;
}

export async function getClientStats(): Promise<ClientFleetStats> {
  const response = await apiClient.get<ApiSuccessEnvelope<ClientFleetStats>>(
    "/super-admin/clients/stats/"
  );
  return response.data.data;
}

export async function getAvailableUsers(search?: string): Promise<AvailableUser[]> {
  const queryParams = new URLSearchParams();
  if (search) queryParams.set("search", search);
  const url = `/super-admin/clients/available-users/?${queryParams.toString()}`;
  const response = await apiClient.get<ApiSuccessEnvelope<AvailableUser[]>>(url);
  return response.data.data;
}

export async function getClientUsers(clientId: string): Promise<ClientLinkedUser[]> {
  const response = await apiClient.get<ApiSuccessEnvelope<ClientLinkedUser[]>>(
    `/super-admin/clients/${clientId}/users/`
  );
  return response.data.data;
}

export async function onboardClient(
  data: ClientOnboardFormValues
): Promise<{ client: ClientDetail; generated_credentials?: { email: string; temporary_password: string } }> {
  // Convert comma-separated emails and mobile numbers to arrays
  const payload = {
    ...data,
    notification_email_ids: data.notification_email_ids_raw
      ? data.notification_email_ids_raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    notification_mobile_nos: data.notification_mobile_nos_raw
      ? data.notification_mobile_nos_raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
  };

  const response = await apiClient.post<
    ApiSuccessEnvelope<{
      client: ClientDetail;
      generated_credentials?: { email: string; temporary_password: string };
    }>
  >("/super-admin/clients/", payload);

  return response.data.data;
}

export async function updateClient(
  clientId: string,
  data: ClientUpdateFormValues
): Promise<ClientDetail> {
  const payload: Record<string, any> = { ...data };
  if (typeof data.notification_email_ids_raw === "string") {
    payload.notification_email_ids = data.notification_email_ids_raw
      ? data.notification_email_ids_raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    delete payload.notification_email_ids_raw;
  }
  if (typeof data.notification_mobile_nos_raw === "string") {
    payload.notification_mobile_nos = data.notification_mobile_nos_raw
      ? data.notification_mobile_nos_raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    delete payload.notification_mobile_nos_raw;
  }

  const response = await apiClient.patch<ApiSuccessEnvelope<ClientDetail>>(
    `/super-admin/clients/${clientId}/`,
    payload
  );
  return response.data.data;
}

export async function updateClientStatus(
  clientId: string,
  status: "active" | "suspended"
): Promise<{ id: string; status: string; updated_at: string }> {
  const response = await apiClient.patch<
    ApiSuccessEnvelope<{ id: string; status: string; updated_at: string }>
  >(`/super-admin/clients/${clientId}/status/`, { status });
  return response.data.data;
}

export async function uploadClientLogo(
  clientId: string,
  file: File
): Promise<{ logo_url: string }> {
  const formData = new FormData();
  formData.append("logo", file);

  const response = await apiClient.post<ApiSuccessEnvelope<{ logo_url: string }>>(
    `/super-admin/clients/${clientId}/logo/`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data.data;
}

export async function deleteClientLogo(
  clientId: string
): Promise<{ logo_url: string }> {
  const response = await apiClient.delete<ApiSuccessEnvelope<{ logo_url: string }>>(
    `/super-admin/clients/${clientId}/logo/`
  );
  return response.data.data;
}

export async function linkUsers(
  clientId: string,
  userIds: string[]
): Promise<{
  client_id: string;
  linked_count: number;
  new_active_user_count: number;
  max_user_limit: number;
}> {
  const response = await apiClient.post<
    ApiSuccessEnvelope<{
      client_id: string;
      linked_count: number;
      new_active_user_count: number;
      max_user_limit: number;
    }>
  >(`/super-admin/clients/${clientId}/users/`, { user_ids: userIds });
  return response.data.data;
}

export async function unlinkUser(
  clientId: string,
  userId: string
): Promise<{ client_id: string; unlinked_user_id: string; new_active_user_count: number }> {
  const response = await apiClient.delete<
    ApiSuccessEnvelope<{
      client_id: string;
      unlinked_user_id: string;
      new_active_user_count: number;
    }>
  >(`/super-admin/clients/${clientId}/users/${userId}/`);
  return response.data.data;
}

export async function deleteClient(clientId: string): Promise<void> {
  await apiClient.delete(`/super-admin/clients/${clientId}/`);
}

// ============================================================================
// TanStack Query Hooks
// ============================================================================

export function useClients(params: ClientsQueryParams) {
  return useQuery({
    queryKey: ["clients", params],
    queryFn: () => getClients(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useClientDetail(clientId: string | null | undefined) {
  return useQuery({
    queryKey: ["client", clientId],
    queryFn: () => getClientDetail(clientId!),
    enabled: Boolean(clientId),
  });
}

export function useClientStats() {
  return useQuery({
    queryKey: ["client-stats"],
    queryFn: getClientStats,
    staleTime: 30_000,
  });
}

export function useAvailableUsers(search?: string, enabled = true) {
  return useQuery({
    queryKey: ["available-users", search],
    queryFn: () => getAvailableUsers(search),
    enabled,
    staleTime: 10_000,
  });
}

export function useClientUsers(clientId: string | null | undefined) {
  return useQuery({
    queryKey: ["client-users", clientId],
    queryFn: () => getClientUsers(clientId!),
    enabled: Boolean(clientId),
  });
}

export function useOnboardClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ClientOnboardFormValues) => onboardClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["available-users"] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: ClientUpdateFormValues;
    }) => updateClient(clientId, data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
    },
  });
}

export function useUpdateClientStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      status,
    }: {
      clientId: string;
      status: "active" | "suspended";
    }) => updateClientStatus(clientId, status),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
    },
  });
}

export function useUploadClientLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, file }: { clientId: string; file: File }) =>
      uploadClientLogo(clientId, file),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
    },
  });
}

export function useDeleteClientLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => deleteClientLogo(clientId),
    onSuccess: (_, clientId) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
    },
  });
}

export function useLinkUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      userIds,
    }: {
      clientId: string;
      userIds: string[];
    }) => linkUsers(clientId, userIds),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-users", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["available-users"] });
    },
  });
}

export function useUnlinkUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      userId,
    }: {
      clientId: string;
      userId: string;
    }) => unlinkUser(clientId, userId),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-users", clientId] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["available-users"] });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => deleteClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client-stats"] });
      queryClient.invalidateQueries({ queryKey: ["available-users"] });
    },
  });
}
